import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, {
  clearAuthAndNotify,
  isInvalidRefreshError,
  revokeCurrentSession,
  refreshAccessToken,
} from "../../shared/api/client";
import { isApiConnectionFailure } from "../../shared/api/helpers";
import { clearDesktopNativeSession, getAuthStorage, isDesktopNativeAuth, storeDesktopAccessToken, storeImpersonationTokens, storeLoginTokens } from "../../shared/api/authStorage";
import { DESKTOP_AUTH_STATE_TYPE, requestDesktopLogout } from "./desktopNativeAuth";
import {
  DESKTOP_PROXY_READY_MESSAGE_TYPE,
  DESKTOP_DEVICE_MESSAGE_TYPE,
  DESKTOP_SHUTDOWN_REQUEST_TYPE,
  desktopProxyAccessKey,
  requestDesktopProxyMetadata,
  parseDesktopProxyRegistration,
  postDesktopApiStatus,
  postDesktopProxyRegisterIdle,
  postDesktopProxyRegisterPending,
  postDesktopProxyRegisterResult,
  postDesktopShutdownResult,
  rememberDesktopDeviceId,
} from "./desktopProxy";
import type { DesktopProxyRegistration } from "./desktopProxy";

const DESKTOP_PROXY_REGISTER_FAILED_MESSAGE =
  "PC 연결 등록에 실패했습니다. 다시 시도해 주세요.";

/** desktop api 램프용 readiness 폴링 간격 */
const API_STATUS_POLL_MS = 15_000;
const DESKTOP_PROXY_METADATA_RETRY_MS = 5_000;
const DESKTOP_PROXY_METADATA_MAX_ATTEMPTS = 3;
type DesktopProxyRegistrationState =
  | "idle"
  | "waiting_metadata"
  | "pending"
  | "ok"
  | "error";


interface LoginData {
  access: string;
  refresh: string;
}

interface SubscriptionStatus {
  is_valid: boolean;
  allow_item_transfer: boolean;
  expired_at: string | null;
  remaining_days: number;
  item_is_premium: boolean;
  max_item_count: number;
  specified_item_limit: number;
  min_item_reprocess_wait_minutes: number;
  registered_item_count: number;
  active_item_count: number;
}

interface ProcessReadinessError {
  code: string;
  message: string;
}

interface ProcessReadiness {
  ready: boolean;
  errors: ProcessReadinessError[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  login: (data: LoginData) => void;
  loginDesktop: (access: string) => void;
  loginImpersonation: (data: LoginData) => void;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  isAccessTokenExpired: () => boolean;
  getRefreshToken: () => string | null;
  refreshToken: () => Promise<boolean>;
  getUsername: () => string | null;
  getUserId: () => number | null;
  isStaff: () => boolean;
  isImpersonating: () => boolean;
  // 결제 상태 관련
  subscriptionStatus: SubscriptionStatus | null;
  isSubscriptionStatusLoaded: boolean;
  isSubscriptionLoading: boolean;
  refreshSubscriptionStatus: () => Promise<void>;
  // 작업 실행 전제조건
  processReadiness: ProcessReadiness | null;
  refreshProcessReadiness: () => Promise<void>;
  /** django API 도달 여부. null=아직 모름, false=연결 실패 */
  apiReachable: boolean | null;
}

interface InitToken {
  access: string;
  refresh: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

// JWT 토큰 디코딩 함수
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT 디코딩 오류:', error);
    return null;
  }
};

export const AuthContextProvider = ({ children, initToken }: { children: React.ReactNode, initToken?: InitToken }) => {

  const isDesktopShell = typeof window !== "undefined" && window.parent !== window;

  if (initToken) {
    storeLoginTokens(initToken);
  }

  const [isAuthenticated, setIsAuthenticated] = useState(
    () => {
      const token = getAuthStorage().getItem('accessToken');
      return token !== null && token !== undefined && token !== '';
    }
  );

  // 결제 상태 관리
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isSubscriptionStatusLoaded, setIsSubscriptionStatusLoaded] = useState(false);
  const [isSubscriptionLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [processReadiness, setProcessReadiness] = useState<ProcessReadiness | null>(null);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [desktopProxy, setDesktopProxy] = useState<DesktopProxyRegistration | null>(null);
  const [desktopProxyRegistrationState, setDesktopProxyRegistrationState] =
    useState<DesktopProxyRegistrationState>("idle");
  const [desktopProxyMetadataWaitActive, setDesktopProxyMetadataWaitActive] =
    useState(false);
  const desktopProxyRequest = React.useRef<string | null>(null);

  useEffect(() => {
    const handleDesktopProxy = (event: MessageEvent) => {
      if (window.parent === window || event.source !== window.parent) {
        return;
      }
      const deviceMsg = event.data as { type?: string; desktopId?: string };
      if (
        deviceMsg?.type === DESKTOP_DEVICE_MESSAGE_TYPE &&
        typeof deviceMsg.desktopId === "string"
      ) {
        rememberDesktopDeviceId(deviceMsg.desktopId);
      }
      if (deviceMsg?.type === DESKTOP_SHUTDOWN_REQUEST_TYPE) {
        void revokeCurrentSession().then((ok) => {
          if (ok) {
            clearAuthAndNotify();
          }
          postDesktopShutdownResult(ok);
        });
        return;
      }
      if (deviceMsg?.type === DESKTOP_AUTH_STATE_TYPE && typeof (deviceMsg as { access?: unknown }).access === "string") {
        const access = (deviceMsg as { access: string }).access;
        if (access) {
          storeDesktopAccessToken(access);
          setIsAuthenticated(true);
        } else if (isDesktopNativeAuth()) {
          clearDesktopNativeSession();
          setIsAuthenticated(false);
        }
        return;
      }
      const registration = parseDesktopProxyRegistration(event.data);
      if (registration) {
        setDesktopProxyMetadataWaitActive(false);
        setDesktopProxy(registration);
      }
    };

    window.addEventListener("message", handleDesktopProxy);
    if (window.parent !== window) {
      window.parent.postMessage({ type: DESKTOP_PROXY_READY_MESSAGE_TYPE }, "*");
    }
    return () => window.removeEventListener("message", handleDesktopProxy);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      desktopProxyRequest.current = null;
      setDesktopProxyRegistrationState("idle");
      return;
    }

    if (isDesktopShell && !desktopProxy) {
      setDesktopProxyRegistrationState("waiting_metadata");
      return;
    }
    if (!desktopProxy) {
      return;
    }

    const requestKey = `${desktopProxy.desktopId}:${desktopProxy.remotePort}`;
    if (desktopProxyRequest.current === requestKey) {
      return;
    }
    desktopProxyRequest.current = requestKey;

    setDesktopProxyRegistrationState("pending");
    postDesktopProxyRegisterPending();
    void desktopProxyAccessKey(desktopProxy.desktopId)
      .then((key) => apiClient.post("/desktop/proxy/", {
        desktop_id: desktopProxy.desktopId,
        remote_port: desktopProxy.remotePort,
      }, { headers: { "X-Desktop-Access-Key": key } }))
      .then(() => {
        if (desktopProxyRequest.current !== requestKey) {
          return;
        }
        setDesktopProxyRegistrationState("ok");
        postDesktopProxyRegisterResult(true);
        void refreshProcessReadiness();
      })
      .catch((error) => {
        if (desktopProxyRequest.current !== requestKey) {
          return;
        }
        if (!getAuthStorage().getItem("accessToken") || isInvalidRefreshError(error)) {
          desktopProxyRequest.current = null;
          setDesktopProxyMetadataWaitActive(false);
          setDesktopProxyRegistrationState("idle");
          postDesktopProxyRegisterIdle();
          return;
        }
        desktopProxyRequest.current = null;
        setDesktopProxyRegistrationState("error");
        console.error("Desktop proxy registration failed:", error);
        // 클라이언트 등록 실패: console 안 infra 문구로 넘기지 않고 셸이 차단
        postDesktopProxyRegisterResult(
          false,
          DESKTOP_PROXY_REGISTER_FAILED_MESSAGE,
        );
      });
  }, [isAuthenticated, desktopProxy]);

  // 자동 로그아웃 이벤트 리스너 등록
  useEffect(() => {
    const handleAuthLogout = () => {
      desktopProxyRequest.current = null;
      setDesktopProxy(null);
      setDesktopProxyMetadataWaitActive(false);
      setDesktopProxyRegistrationState("idle");
      postDesktopProxyRegisterIdle();
      setIsAuthenticated(false);
      setSubscriptionStatus(null);
      setIsSubscriptionStatusLoaded(false);
      setProcessReadiness(null);
      setApiReachable(null);
    };

    window.addEventListener('auth-logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []);

  // 인증 상태가 변경될 때 결제·전제조건 상태 로드
  useEffect(() => {
    if (isAuthenticated) {
      refreshSubscriptionStatus();
      if (isDesktopShell) {
        setDesktopProxyRegistrationState("waiting_metadata");
        setDesktopProxyMetadataWaitActive(true);
        postDesktopProxyRegisterPending();
        requestDesktopProxyMetadata();
      } else {
        refreshProcessReadiness();
      }
    } else {
      setDesktopProxy(null);
      desktopProxyRequest.current = null;
      setDesktopProxyMetadataWaitActive(false);
      setDesktopProxyRegistrationState("idle");
      postDesktopProxyRegisterIdle();
      setSubscriptionStatus(null);
      setIsSubscriptionStatusLoaded(false);
      setProcessReadiness(null);
      setApiReachable(null);
    }
  }, [isAuthenticated]);

  // 인증 직후 metadata가 누락되면 제한 횟수만 재요청하고 shell에 실패를 알린다.
  useEffect(() => {
    if (
      !isAuthenticated ||
      !isDesktopShell ||
      !desktopProxyMetadataWaitActive ||
      desktopProxy
    ) {
      return;
    }

    let attempts = 1;
    let timer: number | undefined;
    const retry = () => {
      if (attempts >= DESKTOP_PROXY_METADATA_MAX_ATTEMPTS) {
        setDesktopProxyMetadataWaitActive(false);
        setDesktopProxyRegistrationState("error");
        postDesktopProxyRegisterResult(
          false,
          "PC 연결 정보를 받지 못했습니다. 데스크탑 앱 연결을 확인해 주세요.",
        );
        return;
      }
      attempts += 1;
      requestDesktopProxyMetadata();
      timer = window.setTimeout(retry, DESKTOP_PROXY_METADATA_RETRY_MS);
    };

    timer = window.setTimeout(retry, DESKTOP_PROXY_METADATA_RETRY_MS);
    return () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [
    isAuthenticated,
    isDesktopShell,
    desktopProxyMetadataWaitActive,
    desktopProxy,
  ]);

  // desktop 셸 api 램프: 도달 상태를 parent에 전달
  useEffect(() => {
    if (!isAuthenticated) {
      postDesktopApiStatus("idle");
      return;
    }
    if (apiReachable === true) {
      postDesktopApiStatus("ok");
    } else if (apiReachable === false) {
      postDesktopApiStatus("error");
    } else {
      postDesktopApiStatus("pending");
    }
  }, [isAuthenticated, apiReachable]);

  // 로그인 중 주기적으로 readiness를 다시 보고 api 램프·배너를 갱신
  useEffect(() => {
    if (
      !isAuthenticated ||
      (isDesktopShell && desktopProxyRegistrationState !== "ok")
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      void refreshProcessReadiness();
    }, API_STATUS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, desktopProxyRegistrationState]);

  const login = (data: LoginData) => {
    storeLoginTokens(data);
    setIsAuthenticated(true);
  };

  const loginDesktop = (access: string) => {
    storeDesktopAccessToken(access);
    setIsAuthenticated(true);
  };

  const loginImpersonation = (data: LoginData) => {
    storeImpersonationTokens(data);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    if (isDesktopNativeAuth()) {
      await requestDesktopLogout();
      clearDesktopNativeSession();
      setIsAuthenticated(false);
      return;
    }
    const storage = getAuthStorage();
    const refresh = storage.getItem('refreshToken');
    await revokeCurrentSession();
    if (getAuthStorage() !== storage || storage.getItem('refreshToken') !== refresh) {
      return;
    }
    storage.removeItem("accessToken");
    storage.removeItem("refreshToken");
    setIsAuthenticated(false);
    setSubscriptionStatus(null);
    setIsSubscriptionStatusLoaded(false);
    setProcessReadiness(null);
    setApiReachable(null);
  };

  const getAccessToken = () => {
    const token = getAuthStorage().getItem('accessToken');
    if (token === null || token === undefined || token === '') {
      setIsAuthenticated(false);
      return null;
    }
    return token;
  }

  const isImpersonating = () => {
    const token = getAuthStorage().getItem('accessToken');
    return Boolean(token && decodeJWT(token)?.impersonated_by);
  };

  const isStaff = () => {
    const token = getAuthStorage().getItem('accessToken');
    const claims = token ? decodeJWT(token) : null;
    return claims?.is_staff === true || claims?.is_superuser === true;
  };

  const isAccessTokenExpired = () => {
    const token = getAccessToken();
    if (!token) {
      return true;
    }

    const payload = decodeJWT(token);
    return !payload?.exp || payload.exp * 1000 <= Date.now();
  }

  const getRefreshToken = () => {
    if (isDesktopNativeAuth()) return null;
    const token = getAuthStorage().getItem('refreshToken');
    if (token === null || token === undefined || token === '') {
      setIsAuthenticated(false);
      return null;
    }
    return token;
  }

  const getUsername = () => {
    const token = getAccessToken();
    if (!token) {
      return null;
    }

    const payload = decodeJWT(token);
    if (payload && payload.username) {
      return payload.username;
    }

    return null;
  }

  const getUserId = () => {
    const token = getAccessToken();
    if (!token) {
      return null;
    }

    const payload = decodeJWT(token);
    if (payload && payload.user_id) {
      return payload.user_id;
    }

    return null;
  }

  // 토큰 갱신 함수 (API 인터셉터와 동일한 refresh 요청 공유)
  const refreshToken = async (): Promise<boolean> => {
    if (isDesktopNativeAuth()) {
      try {
        const access = await refreshAccessToken();
        if (access) setIsAuthenticated(true);
        return Boolean(access);
      } catch { return false; }
    }
    const refresh = getRefreshToken();
    if (!refresh) {
      logout();
      return false;
    }

    try {
      await refreshAccessToken();
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (isInvalidRefreshError(error) && getAuthStorage().getItem('refreshToken') === refresh) {
        logout();
      }
      return false;
    }
  };

  // 결제 상태 갱신 함수 (apiClient 인터셉터가 401 시 refresh 처리)
  const refreshSubscriptionStatus = async (): Promise<void> => {
    if (!isAuthenticated) {
      setSubscriptionStatus(null);
      setIsSubscriptionStatusLoaded(false);
      return;
    }

    setIsPaymentLoading(true);
    try {
      const response = await apiClient.get('/account/subscription/status/');
      const result = response.data;
      if (result.success) {
        setSubscriptionStatus(result.data);
      } else {
        setSubscriptionStatus(null);
      }
    } catch (error) {
      console.error('Subscription status fetch failed:', error);
      setSubscriptionStatus(null);
    } finally {
      setIsPaymentLoading(false);
      setIsSubscriptionStatusLoaded(true);
    }
  };

  const refreshProcessReadiness = async (): Promise<void> => {
    if (!isAuthenticated) {
      setProcessReadiness(null);
      return;
    }

    try {
      const response = await apiClient.get('/account/process-readiness/');
      setProcessReadiness(response.data);
      setApiReachable(true);
    } catch (error) {
      console.error('Process readiness fetch failed:', error);
      if (isApiConnectionFailure(error)) {
        setApiReachable(false);
      }
      // processReadiness는 유지 (마지막 성공 값 또는 null)
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      login,
      loginDesktop,
      loginImpersonation,
      logout,
      getAccessToken,
      isAccessTokenExpired,
      getRefreshToken,
      refreshToken,
      getUsername,
      getUserId,
      isStaff,
      isImpersonating,
      subscriptionStatus,
      isSubscriptionStatusLoaded,
      isSubscriptionLoading,
      refreshSubscriptionStatus,
      processReadiness,
      refreshProcessReadiness,
      apiReachable,
    }}>
      {children}
    </AuthContext.Provider>
  );

};



export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export type { AuthContextType, SubscriptionStatus, ProcessReadiness, ProcessReadinessError };
